import { Vec3 } from 'ayvri-std/lib/vector';
/**
 * Trigger Predicates
 */
const predicateTable = {};
// True if ALL the predicates in the options object are true
predicateTable.and = function and(options, state, app) {
    const predicates = Object.keys(options);
    const predCount = predicates.length;
    for (let i = 0; i < predCount; i++) {
        const predName = predicates[i];
        if (!predicateTable[predName](options[predName], state, app)) {
            return false;
        }
    }
    return true;
};
// True for one frame when we play past the specified time. Not true if the user scrubs past the specified time.
predicateTable.atEpochTime = function onEpochTime(options, state) {
    const { epochTime } = options;
    const scrubbing = !state.clock.playing;
    if (scrubbing)
        return state.clock.time === epochTime;
    else
        return state.clock.time > epochTime && state.clock.prevTime <= epochTime;
};
// True whenever the scene time is after the specified time, regardless of scrubbing or playing.
predicateTable.afterEpochTime = function onEpochTime(options, state) {
    const { epochTime } = options;
    return state.clock.time >= epochTime;
};
// True whenever the scene time is before the specified time, regardless of scrubbing or playing.
predicateTable.beforeEpochTime = function onEpochTime(options, state) {
    const { epochTime } = options;
    return state.clock.time < epochTime;
};
// True while another event is active
predicateTable.whileEventActive = function whileEventActive(options) {
    const { event } = options;
    return event.active;
};
// True while the camera is within options.distance of options.targetPos
const scratchVec = new Vec3();
predicateTable.onCameraProximity = function onActivitiesProximity(options, state, app) {
    const { targetPos, distance } = options;
    const camPos = app.camera.pos;
    const dist = Vec3.subtract(camPos, targetPos, scratchVec).length();
    return dist < distance;
};
// True whenever p2 has travelled farther than p1
predicateTable.onActivityDistanceSurpass = function onActivitiesProximity(options, state) {
    const { activity1, activity2 } = options;
    const p1 = state.playheads[activity1];
    const p2 = state.playheads[activity2];
    if (p1.ready && p2.ready) {
        return p2.cumulative > p1.cumulative;
    }
    else {
        return false;
    }
};
/**
 * Triggers an {@link Event} when a certain condition is met. This condition is evaluated using a specific function,
 * specified by options.predicate. When the predicate returns true for the first time, {@link Event#start} is called.
 * If at some later point the predicate returns false (e.g. the user has scrubbed backward in the timeline),
 * {@link Event#undo} is called. Much freedom is left to the Event to determine what to do in these cases.
 *
 * @param {String} predicateName Used as a lookup into the predicateTable.
 * @param {Event} event The {@link Event} that will managed by this trigger.
 * @param {Object} options Properties passed to the predicate when it is called. See predicate definitions for details.
 */
export default class Trigger {
    constructor(predicateName, event, options) {
        this.options = options;
        this.fired = false;
        this.event = event;
        // Set up the function that will determine when this trigger fires
        this.predicate = predicateTable[predicateName];
        if (!this.predicate)
            throw new Error('Invalid Trigger:', predicateName);
    }
    update(app) {
        const state = app.store.getState();
        const active = this.predicate(this.options, state, app);
        if (!this.fired && active) {
            this.event.start(app);
            this.fired = true;
        }
        if (this.fired && !active) {
            this.event.undo(app);
            this.fired = false;
        }
    }
}
